import { NEVER_DIRTY, OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { FinancialDetailsHeader } from '@client/components/claims/financials/FinancialDetailsHeader';
import { usePaymentApprovalEnabled } from '@client/components/claims/financials/payment-approval/use-payment-approval-enabled';
import { ICONS } from '@client/constants/icons';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { claimFormStore } from '@client/routes/~console.$orgId/~claims/~details.$id/store';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Typography } from 'antd';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Reserve details',
        },
    },
    params: {
        parse({ reserveId }) {
            return {
                reserveId: BigInt(reserveId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReserveDetails />
        </PageController>
    );
}

function ReserveDetails() {
    const orgId = useOrgId();
    const paymentApprovalEnabled = usePaymentApprovalEnabled();
    const { id, reserveId } = Route.useParams();
    const { data } = trpc.reserve.getReserve.useQuery({ id: reserveId });
    const { formatCurrency } = useFormatter();
    // CUR-2676 (PAY-L3): nothing else changes a reserve a final payment locks or is paid from it, so Add adjustment
    // and Add payment are disabled with the reason, as the reserves table's Shortcuts are.
    const lockedReason =
        data?.finalPaymentLock == null
            ? undefined
            : `${data.costCodeFullName ?? 'This reserve'} closes once the final payment of ${formatCurrency(data.finalPaymentLock.amount)} to ${data.finalPaymentLock.payeeName} is paid. Nothing else can change it or be paid from it until then.`;
    const navigate = useNavigate();
    const isReadonly = claimFormStore.use.isReadonly();

    return (
        <OverlayPage
            isDirty={NEVER_DIRTY}
            title={<CardTitle icon={<ICONS.reserve />} title="Reserve details" />}
            size="xLarge"
            afterClose={() => navigate({ moveInMatches: -1 })}
        >
            <FinancialDetailsHeader
                data={{
                    costCode: data?.costCodeFullName,
                    createdAt: data?.createdAt,
                    CreatedBy: {
                        userId: data?.CreatedBy?.userId,
                        photoUrl: data?.CreatedBy?.photoUrl,
                        fullName: data?.CreatedBy?.fullName,
                    },
                }}
                rows={[
                    [
                        {
                            title: 'Total reserve',
                            type: 'currency',
                            children: data?.totalReserveAmount,
                        },
                        {
                            title: 'Total payments',
                            type: 'currency',
                            children: data?.compensationPaymentsAmountSum,
                        },
                        {
                            title: 'Remaining reserve',
                            type: 'currency',
                            children: data?.remainingReserveAmount,
                        },
                    ],
                ]}
            />

            <div className="mb-4 rounded-(--ant-rounded-lg) bg-muted p-4">
                <Typography.Text>{data?.description}</Typography.Text>
            </div>

            <Tabs
                // CUR-2675: no adding on a read-only claim (closed, or no claim write access); the add
                // routes would only send the viewer back.
                rightAddon={
                    !isReadonly && (
                        <Button.Add.Link
                            to="/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/reserve-adjustments/add"
                            params={{
                                orgId,
                                id,
                                reserveId,
                            }}
                            variant="tertiary"
                            label="Add adjustment"
                            disabled={lockedReason !== undefined}
                            disabledReason={lockedReason}
                        />
                    )
                }
                primaryActionAddon={
                    // CUR-2675: no adding on a read-only claim. CUR-2672: with payment approval, Add payment
                    // opens the new drawer with this reserve chosen.
                    !isReadonly &&
                    (paymentApprovalEnabled ? (
                        <Button.Add.Link
                            to="/console/$orgId/claims/details/$id/financials/payments/add"
                            params={{ orgId, id }}
                            search={{ reserveId: reserveId.toString() }}
                            variant="primary"
                            label="Add payment"
                            disabled={lockedReason !== undefined}
                            disabledReason={lockedReason}
                        />
                    ) : (
                        <Button.Add.Link
                            to="/console/$orgId/claims/details/$id/financials/compensations/add"
                            params={{
                                orgId,
                                id,
                            }}
                            variant="primary"
                            label="Add payment"
                        />
                    ))
                }
                items={[
                    {
                        label: 'Movements',
                        to: '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/reserve-adjustments',
                    },
                    {
                        label: 'Linked payments',
                        to: '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/compensation-payments',
                    },
                    {
                        label: 'Library',
                        to: '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/library',
                    },
                ]}
            />
        </OverlayPage>
    );
}
